import React, { useContext, useState, Suspense} from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { Box, Container, Typography, Link, Alert  } from "@mui/material";
import NavBar from "./components/NavBar";
import LoginForm from "./components/LoginForm";
import { AuthContext } from "./contexts/AuthContext";
import {
  regularRoutes,
  adminRoutes,
  serviceProviderRoutes,
} from "./utils/routes";
import RegisterForm from "./components/RegisterForm";
import ForgotPasswordForm from "./components/ForgotPasswordForm";
import ValidateResetToken from "./components/ValidateResetToken";
import ResetPasswordForm from "./components/ResetPasswordForm";
import ValidateInviteToken from "./components/ValidateInviteToken";
import RegisterExistingMember from './components/RegisterExistingMember';

import { APP_VERSION } from './version';
import { getAppVersion } from './getVersion';

function Copyright() {
  return (
    <Container sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        variant="body2"
        align="center"
        sx={{
          color: "text.secondary",
          display: 'inline',
          flexGrow: 1,
        }}
      >
        {"Copyright © "}
        <Link color="inherit" href="https://www.duostechnologies.com/">
          Duos Technologies, Inc.
        </Link>{" "}
        {new Date().getFullYear()}
        {" All Rights Reserved."}
      </Typography>

      <Typography
        variant="caption"
        align="right"
        sx={{
          color: "text.secondary",
          display: 'inline'
        }}
      >
        {getAppVersion()}
      </Typography>
    </Container>
  );
}

function ProtectedRoute({ children }) {
  const { isLoggedIn } = useContext(AuthContext);
  return isLoggedIn ? children : <Navigate to="/login" />;
}

function AdminRoute({ children, setUnauthorizedMessage }) {
  const { isLoggedIn, isAdmin } = useContext(AuthContext);
  if (isLoggedIn === undefined || isAdmin === undefined) {
    return <div>Loading...</div>; // Or a spinner
  }

  if (!isLoggedIn || !isAdmin) {
    if (setUnauthorizedMessage) {
      setUnauthorizedMessage("You are not authorized to access this area.");
    }
    return (
      <Navigate
        to="/"
        replace
        state={{
          unauthorizedMessage: "You are not authorized to access this area.",
        }}
      />
    );
  }
  // If the user is logged in and is an admin, render the children

  return children;
}

function ServiceProviderRoute({ children, setUnauthorizedMessage }) {
  const { isLoggedIn, isServiceProvider } = useContext(AuthContext);
  if (isLoggedIn === undefined || isServiceProvider === undefined) {
    return <div>Loading...</div>; // Or a spinner
  }

  if (!isLoggedIn || !isServiceProvider) {
    if (setUnauthorizedMessage) {
      setUnauthorizedMessage("You are not authorized to access this area.");
    }
    return (
      <Navigate
        to="/"
        replace
        state={{
          unauthorizedMessage: "You are not authorized to access this area.",
        }}
      />
    );
  }
  // If the user is logged in and is an admin, render the children

  return children;
}

function App() {
  const { isAdmin, isServiceProvider, globalError, setGlobalError } = useContext(AuthContext);
  const [isAdminMode, setIsAdminMode] = useState(false); // Track admin mode
  // Only need the setter passed into nested routes; value not directly rendered here
  const [, setUnauthorizedMessage] = useState("");

  const toggleAdminMode = () => {
    setIsAdminMode((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "98vh",
      }}
    >
      <Router>
        <NavBar isAdminMode={isAdminMode} toggleAdminMode={toggleAdminMode} />
        <Container
          maxWidth={false}
          component="main"
          sx={{ flexGrow: 1, mt: 2 }}
        >
          {/* Display global error if it exists */}
          {globalError !== "" && (
            <Alert
              severity="error"
              onClose={() => setGlobalError("")} // Allow dismissing the error
              sx={{ mb: 2 }}
            >
              {globalError}
            </Alert>
            //   <div style={{ color: "red", marginBottom: "16px" }}>
            //   {globalError}
            // </div>
          )}
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route path="/forgot-password" element={<ForgotPasswordForm />} />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/validate-reset-token" element={<ValidateResetToken />} />
              <Route path="/member/validate-invitation-token" element={<ValidateInviteToken />} />
              <Route path="/member/member-register" element={<RegisterForm />} />
              <Route path="/member/register-existing-member" element={<RegisterExistingMember />} />
              
              {/* Admin Routes */}
              {isAdminMode && isAdmin && (
                <Route
                  path="/admin/*"
                  element={
                    <AdminRoute setUnauthorizedMessage={setUnauthorizedMessage}>
                      <Routes>
                        {adminRoutes.map((route) => (
                          <Route
                            key={route.name}
                            path={route.path.replace("/admin", "")}
                            element={
                              <ProtectedRoute>{route.component}</ProtectedRoute>
                            }
                          />
                        ))}
                      </Routes>
                    </AdminRoute>
                  }
                />
              )}
              {/* Regular Routes */}
              {!isAdminMode &&
                !isServiceProvider &&
                regularRoutes.map((route) => (
                  <Route
                    key={route.name}
                    path={route.path}
                    element={<ProtectedRoute>{route.component}</ProtectedRoute>}
                  />
                ))}
              {isServiceProvider && (
                <Route
                  path="/admin/*"
                  element={
                    <ServiceProviderRoute
                      setUnauthorizedMessage={setUnauthorizedMessage}
                    >
                      <Routes>
                        {serviceProviderRoutes.map((route) => (
                          <Route
                            key={route.name}
                            path={route.path.replace("/admin", "")}
                            element={
                              <ProtectedRoute>{route.component}</ProtectedRoute>
                            }
                          />
                        ))}
                      </Routes>
                    </ServiceProviderRoute>
                  }
                />
              )}

              {/* Catch Unauthorized Access to /admin */}
              {!isAdmin && (
                <Route
                  path="/admin/*"
                  element={
                    <Navigate
                      to="/"
                      replace
                      state={{
                        unauthorizedMessage:
                          "You are not authorized to access this area.",
                      }}
                    />
                  }
                />
              )}

              {/* Catch-All Route for Invalid Paths */}
            </Routes>
          </Suspense>
        </Container>
        <Box component="footer" sx={{ py: 3, px: 2, mt: "auto" }}>
          <Copyright />
        </Box>
      </Router>
    </Box>
  );
}

export default App;
